import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { enabledDisciplines } from '@structural-climbing/validation'
import { Button, LanguageSwitcher, UpdateBanner } from '@structural-climbing/ui'
import { LegalDocumentGate } from '../features/legal/LegalDocumentGate'
import { PrivacyPolicyLink } from '../features/legal/PrivacyPolicyLink'
import { useProfileCompletion } from '../context/ProfileCompletionContext'
import { useNews } from '../features/news/useNews'
import { splitFeed, liveEventCount, unseenNewsCount } from '../features/news/newsSelectors'
import { liveCompetitionCount, useCompetitions } from '../features/competitions/useCompetitions'
import { useViewedGym } from '../context/ViewedGymContext'
import { useMyGyms } from '../features/gym/useMyGyms'
import { useGymBranding } from '../features/gym/useGymBranding'
import { useMyMemberships } from '../features/gym/useMyMemberships'
import { useMyClasses } from '../features/classes/useMyClasses'
import { useMemberRealtime } from '../features/gym/useMemberRealtime'
import { useSeenNews } from '../context/SeenNewsContext'
import { AppHeader } from '../features/nav/AppHeader'
import { GymBar } from '../features/gym/GymBar'
import { useGymOccupancy } from '../features/gym/useGymOccupancy'
import { BottomTabBar } from '../features/nav/BottomTabBar'
import { DesktopSidebar } from '../features/nav/DesktopSidebar'
import { GymSwitcherSheet } from '../features/nav/GymSwitcherSheet'
import { type DotKind } from '../features/nav/NavDot'
import {
  groupForPath,
  rememberRoute,
  visibleGroups,
  type DotKey,
  type NavGroupId,
} from '../features/nav/navGroups'
import { fadeRise } from '../design/motion'

export function RootLayout() {
  const { isAuthenticated, loginWithRedirect, logout, user, getAccessTokenSilently } = useAuth()
  const gymId = user?.['https://structuralclimbing.com/gym_id']
  const { gym: viewedGym } = useViewedGym()
  const { completion } = useProfileCompletion()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { gymSlug } = useParams<{ gymSlug?: string }>()
  const [switcherOpen, setSwitcherOpen] = useState(false)

  // `/join/:gymSlug` and `/guest/:gymSlug` name a gym in the URL too, but the
  // visitor is not a member of it yet, so its pages must stay out of the menu.
  const inGymRoute = !pathname.startsWith('/join/') && !pathname.startsWith('/guest/')
  const routeGymSlug = inGymRoute ? gymSlug : undefined

  // Falls back through: the gym in the URL, the last one opened, then whichever
  // gyms the member actually belongs to. That last step matters for someone
  // just approved at their first gym, who has a gym but has never opened one —
  // without it their whole menu stays hidden until they visit a gym page.
  const { gyms: myGyms, loading: gymsLoading, refresh: refreshMyGyms } = useMyGyms()
  // A child's own login, invited by a parent. Null for everyone else, and it
  // shares the request `useMyGyms` already makes.
  const isChildAccount = useMyMemberships().childAccount !== null

  // Mounted at the shell, not on a page: a member waiting to be approved can be
  // anywhere in the app when it happens, and the nav has to grow under them.
  useMemberRealtime()

  // Until the list has loaded, trust what was remembered rather than flashing a
  // stripped-down menu on every navigation.
  const gymsKnown = !gymsLoading
  const rememberedGym =
    viewedGym && (!gymsKnown || myGyms.some((g) => g.slug === viewedGym.slug)) ? viewedGym : null
  const fallbackGym = myGyms.find((g) => g.slug === rememberedGym?.slug) ?? myGyms[0] ?? null
  const navGymSlug = routeGymSlug ?? fallbackGym?.slug ?? null
  const activeGym = myGyms.find((g) => g.slug === navGymSlug) ?? fallbackGym
  // Whether the gym on screen is one of the member's own. The documents gate
  // used to wait for a `gym_id` in the login token, which approval only writes
  // for the *next* login: someone approved while signed in used the app with
  // nothing signed until they happened to sign in again.
  const belongsToNavGym = myGyms.some((g) => g.slug === navGymSlug)
  // News is not one of a child account's screens, so it is not fetched either.
  const newsGymId = isChildAccount ? null : (fallbackGym?.id ?? (routeGymSlug ? gymId : null))

  const { gym, global } = useNews(newsGymId)
  const { seenNews } = useSeenNews()
  const { gymEvents, communityEvents, gymNews, communityNews, appUpdates } = splitFeed({
    gym,
    global,
  })

  // No gym id: the same list the Competitions page loads, so the two share one
  // request through the fetch cache rather than each paying for their own.
  const { competitions } = useCompetitions(
    null,
    import.meta.env.VITE_API_URL as string,
    getAccessTokenSilently,
  )

  // Two different clocks. A competition and an event are retired by the calendar
  // — they stop mattering when they are over, and nothing a member does before
  // then makes them stop mattering sooner. News is retired by being read.
  const competitionCount = liveCompetitionCount(competitions)
  const eventsCount = liveEventCount([...gymEvents, ...communityEvents])
  const newsCount = unseenNewsCount([...gymNews, ...communityNews, ...appUpdates], seenNews)
  const badges = { events: eventsCount, news: newsCount, competitions: competitionCount }

  // The cached list revalidates on mount, and this layout never unmounts, so a
  // member approved at a gym while the app was open would keep reading the empty
  // list it loaded at sign-up. Seeing a gym the list does not know about is the
  // signal that it is out of date; refresh once per unknown gym so it cannot loop.
  const presentGymSlug = routeGymSlug ?? viewedGym?.slug ?? null
  const refreshedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!presentGymSlug || gymsLoading) return
    if (myGyms.some((g) => g.slug === presentGymSlug)) return
    if (refreshedFor.current === presentGymSlug) return
    refreshedFor.current = presentGymSlug
    void refreshMyGyms()
  }, [presentGymSlug, gymsLoading, myGyms, refreshMyGyms])

  // Remember where you were inside each tab, so coming back to Community lands
  // on the standings you were reading rather than resetting to news.
  const activeGroup = groupForPath(pathname)
  useEffect(() => {
    if (activeGroup) rememberRoute(activeGroup, pathname)
  }, [activeGroup, pathname])

  // The gym the nav points at decides which discipline pills it gets. Read off
  // the same cached /gym-info the gym's own screens use; until it arrives the
  // nav shows both, which is what every gym had before.
  const navBranding = useGymBranding(
    myGyms.find((g) => g.slug === navGymSlug)?.id ?? null,
    import.meta.env.VITE_API_URL as string,
  )
  // A child's own login holds no plan, so it never books; no request for it.
  const { eligible: canBookClasses } = useMyClasses(isChildAccount ? null : (activeGym?.id ?? null))
  const groups = visibleGroups(navGymSlug, {
    childAccount: isChildAccount,
    classes: canBookClasses,
    disciplines: enabledDisciplines(navBranding?.disciplines),
  })
  // Home's header leads with the gym. Occupancy is only asked for there, and
  // shares its cached request with the page.
  const onHome = activeGroup === 'home' && navGymSlug !== null
  // The gym's own page opens over the whole screen with its cover at the top,
  // and the ideas campaign brings its own back button.
  const onGymPage = /^\/[^/]+\/(gym|ideas)\/?$/.test(pathname)
  const occupancy = useGymOccupancy(
    onHome ? (myGyms.find((g) => g.slug === navGymSlug)?.id ?? null) : null,
    import.meta.env.VITE_API_URL as string,
  )
  const currentGroup = groups.find((g) => g.id === activeGroup)
  const sectionItems = currentGroup ? currentGroup.items(navGymSlug) : []

  // Dots, not counts. An incomplete profile has nothing to count in the first
  // place, and neither does unread news: the number never told a thumb anything
  // it was going to act on differently. Each clears on its own terms — the
  // profile at 100%, news once its tab has been read, competitions and events
  // once they are over. The sidebar keeps the counts; it has the room.
  // A child cannot fill their profile in, so there is nothing to nag them about.
  const profileIncomplete = completion < 100 && !isChildAccount
  const groupDots: Partial<Record<NavGroupId, DotKind | false>> = {
    me: profileIncomplete && 'attention',
    climb: competitionCount > 0 && 'new',
    community: eventsCount + newsCount > 0 && 'new',
  }
  // The tab dot says "something in here"; these say which entry.
  const sectionDots: Partial<Record<DotKey, DotKind | false>> = {
    profile: profileIncomplete && 'attention',
    news: newsCount > 0 && 'new',
    events: eventsCount > 0 && 'new',
    competitions: competitionCount > 0 && 'new',
  }

  const signOut = () => logout({ logoutParams: { returnTo: window.location.origin } })

  if (!isAuthenticated) {
    // h-dvh with the scrolling inside <main>, like the signed-in shell below,
    // and not min-h-dvh. A guest signs the gym's documents on this shell, and
    // the document viewer is sized by a chain of flex-1 that only resolves
    // against a definite height. Under min-h-dvh there was none, so the PDF's
    // iframe fell back to the browser default of 150px: a guest was asked to
    // sign a document they could see a strip of.
    return (
      <div className="flex h-dvh flex-col bg-[var(--gym-page)] text-foreground">
        <UpdateBanner message={t('appUpdate.message')} actionLabel={t('appUpdate.action')} />
        <header className="flex items-center justify-between border-b border-[var(--gym-line)] px-4 py-3">
          <div className="flex items-center gap-2">
            <img
              src="/icons/symbol-structural.png"
              alt="Structural"
              className="h-8 w-auto flex-shrink-0"
            />
            <img src="/icons/text-structural.png" alt="" className="h-[12px] w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button size="sm" color="primary" onClick={() => loginWithRedirect()}>
              {t('common.signIn')}
            </Button>
          </div>
        </header>
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
        {/* Where someone meets the app before they have an account. shrink-0
            so it sits under the page and never inside the scrolling area. */}
        <footer className="flex shrink-0 justify-center px-4 py-1.5">
          <PrivacyPolicyLink />
        </footer>
      </div>
    )
  }

  // h-dvh, not h-screen. 100vh is the viewport with the browser's URL bar
  // collapsed, and this app scrolls an inner div rather than the document —
  // exactly the case where a mobile browser never collapses that bar. The shell
  // was therefore permanently taller than the screen by the height of the bar,
  // and the tab bar, pinned to the bottom of the shell, sat below the fold until
  // you scrolled. The dynamic unit tracks what is actually visible, and because
  // the scrolling is internal it does not then change under you.
  return (
    <div className="flex h-dvh bg-[var(--gym-page)] text-foreground">
      <DesktopSidebar
        groups={groups}
        gymSlug={navGymSlug}
        gym={activeGym}
        canSwitch={myGyms.length > 1}
        onSwitchGym={() => setSwitcherOpen(true)}
        badges={badges}
        profileIncomplete={profileIncomplete}
        onSignOut={signOut}
      />

      {/* The scroll container. The tab bar is sticky inside it rather than
          fixed outside, so iOS Safari's collapsing address bar cannot leave a
          gap underneath it. */}
      <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
        <UpdateBanner message={t('appUpdate.message')} actionLabel={t('appUpdate.action')} />
        <AppHeader
          lead={
            onHome && !onGymPage && navBranding ? (
              <GymBar
                branding={navBranding}
                occupancy={occupancy}
                gymSlug={navGymSlug}
                className="px-4 pb-1 pt-3"
              />
            ) : undefined
          }
          items={onGymPage ? [] : sectionItems}
          dots={sectionDots}
        />

        <main className="flex flex-1 flex-col">
          {/* Animates in on mount, and nothing more.
              
              This used to be an AnimatePresence with mode="wait", which is the
              usual recipe for page transitions and is wrong here: Outlet is not
              a snapshot. The moment the route changes it renders the new page
              inside the old key, and mode="wait" then holds the real wrapper
              back until the old one has finished exiting — so the page mounted,
              fired its requests, unmounted and mounted again. Landing on an
              empty screen was that race losing.

              Keyed on the group, not the pathname, so moving between two
              screens of one tab reads as the page updating rather than as the
              whole app being replaced. */}
          <motion.div
            key={activeGroup ?? pathname}
            variants={fadeRise}
            initial="hidden"
            animate="visible"
            className="flex flex-1 flex-col"
          >
            <Outlet />
          </motion.div>
        </main>

        {/* Sticky inside the scroll container, so iOS Safari's collapsing
            address bar cannot leave a gap under it. The Routes / Standings bar
            that used to sit on top of it is gone: Standings opens from the rank
            card on the wall. */}
        <div className="sticky bottom-0 z-[var(--z-nav)] md:hidden">
          {/* The page fades out above the tab bar instead of being cut by it. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 -top-10 h-10"
            style={{ background: 'linear-gradient(to bottom, transparent, var(--gym-page))' }}
          />
          <BottomTabBar groups={groups} gymSlug={navGymSlug} dots={groupDots} />
        </div>
      </div>

      <GymSwitcherSheet
        isOpen={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        gyms={myGyms}
        activeSlug={navGymSlug}
        childAccount={isChildAccount}
      />

      {/* Not for a child's own login: documents are the parent's to sign. */}
      {isAuthenticated &&
        (gymId || belongsToNavGym) &&
        !isChildAccount &&
        !pathname.startsWith('/join/') &&
        !pathname.startsWith('/guest/') && <LegalDocumentGate gymSlug={navGymSlug} />}
    </div>
  )
}
